import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { motion } from 'framer-motion';
import { Clock, CheckCircle, AlertCircle, Heart, FileText } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const FollowUpTimeline = ({ followUps, originalDate }) => {
  if (!followUps || followUps.length === 0) {
    return null;
  }

  const getIcon = (type) => {
    switch (type) {
      case 'answer':
        return <CheckCircle size={18} className="text-[#3AAB8F]" />;
      case 'testimony':
        return <Heart size={18} className="text-[var(--sd-primary)]" />;
      case 'challenge':
        return <AlertCircle size={18} className="text-[#D89B2D]" />;
      default:
        return <FileText size={18} className="text-[#4A7FB8]" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'answer':
        return 'Answer Received';
      case 'testimony':
        return 'Testimony';
      case 'challenge':
        return 'Challenge';
      default:
        return 'Update';
    }
  };

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'answer':
        return 'bg-[#3AAB8F]/15 text-[#1F5D4C] border border-[#3AAB8F]/40 font-semibold';
      case 'testimony':
        return 'bg-[var(--sd-primary)]/15 text-[var(--sd-primary-dark)] border border-[var(--sd-primary)]/40 font-semibold';
      case 'challenge':
        return 'bg-[#D89B2D]/15 text-[#8A5E12] border border-[#D89B2D]/40 font-semibold';
      default:
        return 'bg-[#4A7FB8]/15 text-[#1E4E7A] border border-[#4A7FB8]/40 font-semibold';
    }
  };

  return (
    <div
      className="mt-6 pt-6 border-t border-[rgba(184,120,132,0.25)]"
      data-testid="followup-timeline"
    >
      <h4 className="text-sm font-bold uppercase tracking-wide text-[var(--sd-primary-dark)] mb-4 flex items-center gap-2">
        <Clock size={16} />
        Reflection Timeline
      </h4>

      <div className="space-y-4 relative pl-8">
        {/* Timeline line */}
        <div className="absolute left-2.5 top-0 bottom-0 w-0.5 bg-[rgba(184,120,132,0.30)]" />

        {/* Original Download */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="relative"
        >
          <div className="absolute -left-8 top-2 w-5 h-5 rounded-full bg-[var(--sd-primary)] border-2 border-white shadow flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-white" />
          </div>
          <Card className="p-4 bg-[rgba(184,120,132,0.06)] border-l-4 border-[var(--sd-primary)] shadow-sm">
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <Badge className="bg-[var(--sd-primary)]/15 text-[var(--sd-primary-dark)] border border-[var(--sd-primary)]/40 font-semibold">
                Original Download
              </Badge>
              <span className="text-xs text-[var(--sd-muted-fg)]">
                {format(new Date(originalDate), 'MMM d, yyyy')}
              </span>
            </div>
            <p className="text-sm text-[var(--sd-fg-secondary)]">
              Initial spiritual insight recorded
            </p>
          </Card>
        </motion.div>

        {/* Follow-ups */}
        {followUps.map((followUp, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="relative"
            data-testid={`followup-entry-${idx}`}
          >
            <div className="absolute -left-8 top-2 w-5 h-5 rounded-full bg-white border-2 border-[var(--sd-primary)] shadow flex items-center justify-center">
              {getIcon(followUp.reflection_type)}
            </div>
            <Card className="p-4 bg-white/80 border border-[rgba(184,120,132,0.25)] shadow-sm">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <Badge className={getTypeBadgeClass(followUp.reflection_type)}>
                  {getTypeLabel(followUp.reflection_type)}
                </Badge>
                <div className="text-xs text-right">
                  <div className="text-[var(--sd-muted-fg)]">
                    {format(new Date(followUp.created_at), 'MMM d, yyyy')}
                  </div>
                  <div className="text-[var(--sd-primary)] font-medium">
                    {formatDistanceToNow(new Date(followUp.created_at), {
                      addSuffix: true,
                    })}
                  </div>
                </div>
              </div>
              <p className="text-sm leading-relaxed text-[#2C2527] whitespace-pre-wrap">
                {followUp.text}
              </p>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Summary */}
      <div className="mt-6 p-4 bg-[rgba(184,120,132,0.08)] rounded-lg border border-[rgba(184,120,132,0.25)]">
        <p className="text-xs text-[var(--sd-fg-secondary)] text-center">
          <span className="mr-1">📅</span>
          <strong className="text-[var(--sd-primary-dark)]">
            {followUps.length}
          </strong>{' '}
          follow-up{followUps.length !== 1 ? 's' : ''} recorded • Tracking divine
          guidance over{' '}
          <strong className="text-[var(--sd-primary-dark)]">
            {formatDistanceToNow(new Date(originalDate))}
          </strong>
        </p>
      </div>
    </div>
  );
};

export default FollowUpTimeline;
